import {noop} from '@yandex-delivery/utils'
import clsx from 'clsx'
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import ReactDOM from 'react-dom'
import {animated, useSpring} from 'react-spring'

import {DEFAULT_BREAKPOINTS, GAP_BETWEEN_HEADER, INITIAL_DRAWER_REF_STATE} from './consts'
import {DrawerContext} from './context'
import {useDrawerStyles} from './styles'
import {Breakpoints, DrawerProps, DrawerRefState} from './types'
import {
  getClosestBreakpoint,
  getCurrentTranslateY,
  isBetweenBounds,
  isValidBreakpointsOptions,
  setElementTransform,
  setOverlayOpacity,
} from './utils'

const getBreakpointDefaults = (closable: boolean): DrawerProps['breakpoints'] => ({
  min: closable ? Breakpoints.HIDDEN : Breakpoints.MEDIUM,
  max: Breakpoints.FULL,
  darken: closable ? Breakpoints.HIDDEN : Breakpoints.LARGE,
  default: closable ? Breakpoints.FULL : Breakpoints.MEDIUM,
  active: closable ? [Breakpoints.HIDDEN, Breakpoints.FULL] : [Breakpoints.MEDIUM, Breakpoints.LARGE, Breakpoints.FULL],
})

const Drawer = React.forwardRef<HTMLDivElement, PropsWithChildren<DrawerProps>>((props, ref) => {
  const {
    children,
    breakpointsCoefficients,
    breakpoints,
    closable = true,
    gap = GAP_BETWEEN_HEADER,
    expanderType = 'inside',
    maxHeight,
    onClose,
    onMax = noop,
    onMin = noop,
    fitContent,
    className,
  } = props
  const classes = useDrawerStyles()
  const [isFirstAnim, setIsFirstAnim] = useState(true)
  const [isFull, setIsFull] = useState(false)
  const [domContainer, setDomContainer] = useState<HTMLDivElement>()
  const [breakpointState, setBreakpointState] = useState<Breakpoints | 'moving'>(Breakpoints.HIDDEN)
  const drawerContext = useContext(DrawerContext)
  const defaultBreakpoints = useMemo(() => getBreakpointDefaults(closable), [closable])
  const breakpointsCustom = useMemo(
    () => ({...defaultBreakpoints, ...(breakpoints || {})}),
    [breakpoints, defaultBreakpoints],
  )
  const refDrawer = useRef<HTMLDivElement>(null)
  const refScroller = useRef<HTMLDivElement>(null)
  const refExpander = useRef<HTMLDivElement>(null)
  const refOverlay = useRef<HTMLDivElement>(null)
  const refOptions = useRef<DrawerRefState>(INITIAL_DRAWER_REF_STATE)
  const [styles, api] = useSpring(() => ({
    y: window.innerHeight,
    height: fitContent ? undefined : maxHeight ?? Math.floor(window.innerHeight * (1 - gap)),
  }))
  const animateDrawer = useCallback(
    (breakpoint: Breakpoints, onEnd?: Function) => {
      api.set({y: getCurrentTranslateY(refDrawer.current)})
      api.start({
        y: refOptions.current.limiter[breakpoint],
        onChange: ({value}) => {
          setOverlayOpacity(
            refOverlay.current,
            value.y,
            refOptions.current.limiter[breakpointsCustom.darken],
            refOptions.current.limiter[breakpointsCustom.max],
          )
        },
        onStart: () => {
          if (refScroller.current) refScroller.current.scrollTop = 0
          setIsFull(false)
        },
        onResolve: () => {
          refOptions.current.movable = true
          setIsFirstAnim(false)
          if (breakpoint === breakpointsCustom.max) {
            setIsFull(true)
          } else {
            setIsFull(false)
          }

          if (breakpoint === breakpointsCustom.max) {
            onMax()
          }

          if (breakpoint === breakpointsCustom.min) {
            onMin()
          }

          const overlay = refOverlay.current
          const drawer = refDrawer.current
          if (overlay?.style && drawer) {
            overlay.style.display =
              getCurrentTranslateY(drawer) >= refOptions.current.limiter[breakpointsCustom.darken] ? 'none' : 'block'
          }

          onEnd && onEnd()
        },
        config: {
          duration: 150,
        },
      })
    },
    [api, breakpointsCustom.darken, breakpointsCustom.max, breakpointsCustom.min, onMax, onMin],
  )
  const handleTouchMove = useCallback(
    e => {
      if (!refOptions.current.movable) {
        return
      }
      setBreakpointState('moving')
      e.stopPropagation()
      refOptions.current.moved = true
      const [touch] = e.changedTouches
      const delta = touch.clientY - refOptions.current.clientY
      const newValue = refOptions.current.currentOffset + delta
      if (
        isBetweenBounds(
          newValue,
          refOptions.current.limiter[Breakpoints.MINIMUM],
          refOptions.current.limiter[Breakpoints.FULL],
        )
      ) {
        setOverlayOpacity(
          refOverlay.current,
          newValue,
          refOptions.current.limiter[breakpointsCustom.darken],
          refOptions.current.limiter[breakpointsCustom.max],
        )
        setElementTransform(refDrawer.current, newValue)
      }
    },
    [setBreakpointState, breakpointsCustom.darken, breakpointsCustom.max],
  )

  const handleTouchStart = useCallback(e => {
    e.stopPropagation()
    refOptions.current.now = Date.now()
    refOptions.current.moved = false
    refOptions.current.initialScreenY = e.type === 'touchstart' ? e.changedTouches[0].screenY : 0
    refOptions.current.clientY = e.type === 'touchstart' ? e.changedTouches[0].clientY : 0
    refOptions.current.currentOffset = getCurrentTranslateY(refDrawer.current)
  }, [])
  const handleTouchEnd = useCallback(
    e => {
      // Был ли это тап по экспандеру
      const isExpanderTapped = !refOptions.current.moved && e.target === refExpander.current

      if (!refOptions.current.movable && !isExpanderTapped) {
        return
      }
      e.stopPropagation()
      const newClientY = getCurrentTranslateY(refDrawer.current)
      let closestBreakpoint = getClosestBreakpoint(refOptions.current.limiter, breakpointsCustom.active, newClientY)
      // Ускорение
      const acceleration =
        Math.abs(refOptions.current.initialScreenY - e.changedTouches[0].screenY) /
        (Date.now() - refOptions.current.now)
      // Направление
      const direction = refOptions.current.initialScreenY - e.changedTouches[0].screenY < 0 ? 'down' : 'up'
      // Быстрый свайп(свернуть или развернуть шторку полностью)
      const isFastSwipe = acceleration > 1

      if (isFastSwipe && direction === 'down') {
        closestBreakpoint = breakpointsCustom.min
      }

      if (isFastSwipe && direction === 'up') {
        closestBreakpoint = breakpointsCustom.max
      }

      if (isExpanderTapped) {
        closestBreakpoint = isFull ? breakpointsCustom.min : breakpointsCustom.max
      }

      if (closestBreakpoint !== breakpointsCustom.max && closable) {
        closestBreakpoint = Breakpoints.HIDDEN
      }
      setBreakpointState(closestBreakpoint)
    },
    [breakpointsCustom.active, breakpointsCustom.max, breakpointsCustom.min, closable, isFull],
  )
  const handleContainerScroll = useCallback(() => {
    refOptions.current.movable = false
  }, [])
  const handleTouchEndScroller = useCallback(() => {
    refOptions.current.movable = refScroller.current ? refScroller.current.scrollTop <= 0 : true
  }, [])
  const handleMouseUp = useCallback(() => {
    setBreakpointState(isFull ? breakpointsCustom.min : breakpointsCustom.max)
  }, [breakpointsCustom.max, breakpointsCustom.min, isFull])

  useImperativeHandle(
    ref,
    // @ts-expect-error - не понимает, что refDrawer.current всегда есть
    () => refDrawer.current,
  )

  // Проверяем,что брэкпоинты валидны
  isValidBreakpointsOptions(
    breakpointsCustom.active,
    breakpointsCustom.min,
    breakpointsCustom.max,
    breakpointsCustom.darken,
    breakpointsCustom.default,
  )

  // Жестко отменяем скролл когда есть компонент шторки
  useEffect(() => {
    const body = window.document.querySelector('body')
    body.style.overflow = 'hidden'
    return () => {
      !closable && (body.style.overflow = '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ждем когда замаунтится DOM элемент для портала
  useEffect(() => {
    if (drawerContext.containerRef) {
      setDomContainer(drawerContext.containerRef.current)
    }
  }, [drawerContext, drawerContext.containerRef])

  // создаем объект брэкпоинтам в пикселях
  useEffect(() => {
    if (domContainer) {
      refOptions.current.limiter = Object.entries(breakpointsCoefficients || DEFAULT_BREAKPOINTS).reduce(
        (acc, [key, value]) => ({
          ...acc,
          /**
           * иногда window.innerHeight * value возвращало .00000000003 (см проблему вычислений с плавающей точкой)
           * поэтому пришлось округлить значение
           * @see https://ru.stackoverflow.com/questions/417453/Вычисления-на-числах-с-плавающей-точкой-работают-неправильно/417454#417454
           */
          [key]: Math.round(window.innerHeight * value),
        }),
        {},
      )
    }
  }, [breakpointsCoefficients, domContainer, breakpointsCustom.active, breakpointsCustom.min])

  useEffect(() => {
    if (!domContainer) {
      return
    }

    if (setBreakpointState) {
      setBreakpointState(breakpointsCustom.default)
    }
  }, [breakpointsCustom.default, domContainer, setBreakpointState])

  // эффект для анимации, шторка притягивается к брэкпоинтам
  // изменения текущего брэкпоинта триггерит анимацию
  // moving обособленный стэйт, не breakpoint
  useEffect(() => {
    if (domContainer && breakpointState && breakpointState !== 'moving') {
      if (animateDrawer) {
        animateDrawer(breakpointState, () => {
          // самый начальный стейт hidden поэтому чтобы
          // не триггерить onClose нужно исключить первый момент
          // появления шторки
          if (!isFirstAnim && breakpointState === Breakpoints.HIDDEN) {
            if (closable && onClose) {
              onClose()
            }
          }
        })
      }
    }
  }, [animateDrawer, breakpointState, breakpointsCustom.darken, domContainer, closable, onClose, isFirstAnim])

  if (!domContainer) {
    return null
  }

  return ReactDOM.createPortal(
    <div>
      <div ref={refOverlay} className={clsx(classes.overlay)} onPointerUp={handleMouseUp} />
      <animated.div
        ref={refDrawer}
        className={clsx(classes.drawer, className)}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        onTouchStart={handleTouchStart}
        style={styles}
      >
        {expanderType !== 'none' && (
          <div className={clsx(classes.expanderWrapper, isFull && expanderType === 'inside' && classes.expanded)}>
            <div
              ref={refExpander}
              className={clsx(classes.expander, isFull && expanderType === 'outside' && classes.expandedOutside)}
              data-e2e="expander"
              onPointerDown={handleTouchStart}
              onPointerUp={handleMouseUp}
            />
          </div>
        )}
        <div
          ref={refScroller}
          className={clsx(classes.scrollContainer, isFull && classes.scrollable)}
          onScroll={handleContainerScroll}
          onTouchEndCapture={handleTouchEndScroller}
        >
          {children}
        </div>
      </animated.div>
    </div>,
    domContainer,
  )
})

export default Drawer
